import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const client = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

const PLACEHOLDER =
  "https://images.unsplash.com/photo-1632496497047-706290273235?w=400&h=400&fit=crop";

export function formatCAD(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(n);
}

export function mapProduct(row) {
  if (!row) return null;
  const price = Number(row.sale_price ?? row.regular_price ?? 0);
  const regularPrice = Number(row.regular_price ?? 0);
  return {
    id: String(row.id),
    sku: row.sku,
    name: row.product_name,
    brand: row.brand || "—",
    price,
    regularPrice,
    onSale: regularPrice > 0 && price > 0 && price < regularPrice,
    image: row.main_image_url || PLACEHOLDER,
    inStock: Boolean(row.in_stock),
    stockStatus: row.stock_status,
    description: row.description,
    compatibleModels: row.compatible_models,
    specifications: row.specifications,
    sourceUrl: row.source_url,
    menuSlug: row.menu_slug,
    menuName: row.menu_name,
    leafSlug: row.leaf_slug,
    leafName: row.leaf_name,
  };
}

export async function fetchMegaMenu() {
  const { data } = await client.get("/catalog/mega-menu");
  return data.sections || [];
}

export async function fetchCatalogStats() {
  const { data } = await client.get("/catalog/stats");
  return data;
}

export async function fetchProducts(params = {}) {
  const { data } = await client.get("/catalog/products", { params });
  return {
    ...data,
    items: (data.items || []).map(mapProduct),
  };
}

export async function fetchProduct(idOrSku) {
  const { data } = await client.get(`/catalog/products/${encodeURIComponent(idOrSku)}`);
  return mapProduct(data);
}

export async function fetchLeafCategories(menuSlug) {
  const { data } = await client.get(`/catalog/menu/${encodeURIComponent(menuSlug)}/leaves`);
  return data.items || [];
}

export async function fetchBrands(params = {}) {
  const { data } = await client.get("/catalog/brands", { params });
  return data.items || [];
}

export function catalogLink({ menu, leaf, q, brand } = {}) {
  const params = new URLSearchParams();
  if (menu) params.set("menu", menu);
  if (leaf) params.set("leaf", leaf);
  if (q) params.set("q", q);
  if (brand) params.set("brand", brand);
  const qs = params.toString();
  return qs ? `/boutique?${qs}` : "/boutique";
}

export async function createCheckoutSession({ items, customer }) {
  const { data } = await client.post("/checkout/create-session", { items, customer });
  return data;
}

export async function fetchOrderStatus(params) {
  const { data } = await client.get("/checkout/order-status", { params });
  return data;
}

export async function trackCall(source = "contact") {
  try {
    await client.post("/track-call", { source });
  } catch {
    /* non-blocking */
  }
}

const ADMIN_TOKEN_KEY = "dc_admin_token";

export function getAdminToken() {
  return localStorage.getItem(ADMIN_TOKEN_KEY) || "";
}

export function setAdminToken(token) {
  if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
  else localStorage.removeItem(ADMIN_TOKEN_KEY);
}

export async function adminLogin(password) {
  const { data } = await client.post("/admin/login", { password });
  if (data.token) setAdminToken(data.token);
  return data;
}

export async function fetchAdminStats(params = {}) {
  const { data } = await client.get("/admin/stats", {
    params,
    headers: { Authorization: `Bearer ${getAdminToken()}` },
  });
  return data;
}

export async function fetchAdminOrders(params = {}) {
  const { data } = await client.get("/admin/orders", {
    params,
    headers: { Authorization: `Bearer ${getAdminToken()}` },
  });
  return data;
}

export async function updateAdminOrder({ id, status, admin_notes }) {
  const { data } = await client.patch(
    "/admin/orders",
    { id, status, admin_notes },
    { headers: { Authorization: `Bearer ${getAdminToken()}` } }
  );
  return data;
}

export async function sendContactMessage(payload) {
  const { data } = await client.post("/contact", payload);
  return data;
}
